import React, { useCallback, useEffect, useRef, useState } from "react"

import { SETTINGS_SECTION_STYLES } from "@/components/settings/_styles"
import type { VenueTagDisplay } from "@/components/venue_tags/types"
import { TAG_CATEGORY_ICONS, CATEGORY_DISPLAY_ORDER, DISPLAY_CATEGORIES } from "@/constants/venue_tags"
import i18n from "@/i18n"
import useIsMobile from "@/shared/use_is_mobile"

type VenueTagsProps = {
  header?: string
  categories: VenueTagDisplay[]
  variant?: "card" | "inline"
  initialVisibleCount?: number
  testId?: string
}

const INITIAL_VISIBLE = 6

const STYLES = {
  scrollerSection: "-mx-4 border-b-[0.5px] border-t-[0.5px] border-[#dbdbdb] bg-[#d9d9d9]/10 pb-[7px] pt-[9px]",
  scroller: "no-scrollbar grid grid-flow-col grid-rows-2 justify-start gap-2 overflow-x-auto px-4",
  card: "flex h-[46px] w-[160px] items-center gap-2 rounded-[6px] border-[0.5px] border-[#e7e7e7] bg-white px-[10px] py-[6px]",
  cardIcon: "flex h-[35px] w-[35px] shrink-0 items-center justify-center rounded-full bg-[#f5f7f9]",
  cardTitle: "text-[11px] leading-[14px] text-[#111827]",
  cardSubtitle: "mt-[3px] truncate text-[8px] leading-[10px] text-[#838fa0]",
  scrollbarTrack: "relative mx-4 mt-[5px] h-[3px]",
  scrollbarThumb: "absolute top-0 h-[3px] rounded-[2px] bg-[#a5a5a5]/30",
} as const

const TagGrid: React.FC<{ categories: VenueTagDisplay[] }> = ({ categories }) => (
  <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
    {categories.map((cat) => {
      const Icon = TAG_CATEGORY_ICONS[cat.category]
      return (
        <div key={cat.category} className="flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] bg-[#f5f7f9]">
            <Icon className="w-[18px] h-[18px] text-[#6b7686]" />
          </div>
          <div className="min-w-0">
            <div className="text-[12px] font-medium leading-[17px] text-black">{cat.label}</div>
            <div className="mt-0 text-[10px] leading-[14px] text-[#616161]">{cat.values}</div>
          </div>
        </div>
      )
    })}
  </div>
)

const TagScroller: React.FC<{ categories: VenueTagDisplay[] }> = ({ categories }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [thumb, setThumb] = useState({ widthPct: 0, leftPct: 0 })

  const updateThumb = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    const { scrollWidth, clientWidth, scrollLeft } = el
    if (scrollWidth <= clientWidth) {
      setThumb({ widthPct: 0, leftPct: 0 })
      return
    }

    setThumb({ widthPct: (clientWidth / scrollWidth) * 100, leftPct: (scrollLeft / scrollWidth) * 100 })
  }, [])

  useEffect(() => {
    updateThumb()

    const el = scrollRef.current
    if (!el || typeof ResizeObserver === "undefined") return

    const observer = new ResizeObserver(updateThumb)
    observer.observe(el)
    return () => observer.disconnect()
  }, [categories, updateThumb])

  return (
    <div className={STYLES.scrollerSection}>
      <div ref={scrollRef} onScroll={updateThumb} className={STYLES.scroller} data-testid="public-venue-tags-scroller">
        {categories.map((cat) => {
          const Icon = TAG_CATEGORY_ICONS[cat.category]
          return (
            <div key={cat.category} className={STYLES.card} data-testid={`public-venue-tags-card-${cat.category}`}>
              <div className={STYLES.cardIcon}>
                <Icon className="w-[18px] h-[18px] text-[#6b7686]" />
              </div>
              <div className="min-w-0">
                <div className={STYLES.cardTitle}>{cat.label}</div>
                <div className={STYLES.cardSubtitle}>{cat.values}</div>
              </div>
            </div>
          )
        })}
      </div>
      {thumb.widthPct > 0 && (
        <div className={STYLES.scrollbarTrack}>
          <div
            className={STYLES.scrollbarThumb}
            style={{ width: `${thumb.widthPct}%`, left: `${thumb.leftPct}%` }}
            data-testid="public-venue-tags-scrollbar"
          />
        </div>
      )}
    </div>
  )
}

const VenueTags: React.FC<VenueTagsProps> = ({
  header,
  categories,
  variant = "card",
  initialVisibleCount = INITIAL_VISIBLE,
  testId,
}) => {
  const [expanded, setExpanded] = useState(false)
  const isMobile = useIsMobile()

  const orderedCategories = CATEGORY_DISPLAY_ORDER.reduce<VenueTagDisplay[]>((acc, key) => {
    const found = categories.find((c) => c.category === key)
    if (found) acc.push(found)
    return acc
  }, [])

  if (orderedCategories.length === 0) return null

  if (variant === "inline") {
    if (isMobile) {
      return (
        <div data-testid="public-venue-tags" className="mb-6">
          <TagScroller categories={orderedCategories} />
        </div>
      )
    }

    const gridCategories = orderedCategories.filter((cat) => DISPLAY_CATEGORIES.includes(cat.category))
    const hasMore = gridCategories.length > initialVisibleCount
    const visible = expanded ? gridCategories : gridCategories.slice(0, initialVisibleCount)

    return (
      <div data-testid="public-venue-tags" className="mb-8 border-t border-[#EFEFEF] pt-6">
        <TagGrid categories={visible} />
        {!!hasMore && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="mt-5 text-sm font-medium text-[#2da0fe] border-none bg-transparent p-0 cursor-pointer"
          >
            {expanded ? `- ${i18n.t("app.venue_tags.show_less")}` : `+ ${i18n.t("app.venue_tags.show_more")}`}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className={SETTINGS_SECTION_STYLES.container} data-testid={testId}>
      <h2 className={SETTINGS_SECTION_STYLES.header} data-testid={testId ? `${testId}-title` : undefined}>
        {header}
      </h2>
      <TagGrid categories={orderedCategories} />
    </div>
  )
}

export default VenueTags
